import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useSite } from "@/lib/SiteContext";

function isActive(pathname, href) {
  if (!href) return false;
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(href + "/");
}

function CtaButton({ item, onClick, className = "" }) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      data-testid={item.testid || `nav-cta-${(item.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`inline-flex items-center justify-center bg-mist-gold text-mist-navy hover:bg-[#b8994f] rounded-md font-semibold uppercase tracking-wide transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mist-gold focus-visible:ring-offset-2 focus-visible:ring-offset-mist-navy ${className}`}
    >
      {item.label}
    </Link>
  );
}

function DesktopDropdown({ item, pathname }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  const anyChildActive = (item.children || []).some((c) => isActive(pathname, c.href));
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        data-testid={item.testid || `nav-${item.label.toLowerCase()}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-medium tracking-[0.02em] transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mist-gold focus-visible:ring-offset-2 focus-visible:ring-offset-mist-navy rounded-sm ${
          anyChildActive ? "text-mist-gold" : "text-white/85 hover:text-white"
        }`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      <div
        role="menu"
        className={`absolute top-full left-0 pt-3 transition-all duration-180 ease-out ${
          open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="bg-white text-mist-ink shadow-[0_20px_50px_-12px_rgba(24,40,74,0.25)] border border-mist-soft rounded-lg overflow-hidden w-[260px] max-w-[calc(100vw-32px)]">
          <ul className="p-3 space-y-0.5">
            {(item.children || []).map((c) => (
              <li key={c.href || c.label}>
                <Link
                  to={c.href || "#"}
                  data-testid={c.testid || `dropdown-link-${(c.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`block px-4 py-3 rounded-md text-[14px] leading-tight font-medium transition-all duration-200 ease-out ${
                    isActive(pathname, c.href)
                      ? "bg-mist-off text-mist-navy"
                      : "text-mist-ink/85 hover:bg-mist-off hover:text-mist-gold"
                  }`}
                  onClick={() => setOpen(false)}
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function MobileAccordion({ item, pathname, onNavigate }) {
  const [open, setOpen] = useState(false);
  if (!item.children || item.children.length === 0) {
    return (
      <Link
        to={item.href || "#"}
        onClick={onNavigate}
        data-testid={`mnav-${item.label.toLowerCase()}`}
        className={`block py-3 text-lg ${
          isActive(pathname, item.href) ? "text-mist-gold" : "text-white/90"
        }`}
      >
        {item.label}
      </Link>
    );
  }
  return (
    <div className="border-b border-white/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full py-3 flex items-center justify-between text-lg text-white"
        aria-expanded={open}
        data-testid={`mnav-toggle-${item.label.toLowerCase()}`}
      >
        <span>{item.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && (
        <ul className="pb-3 pl-3">
          {item.children.map((c) => (
            <li key={c.href || c.label}>
              <Link
                to={c.href || "#"}
                onClick={onNavigate}
                data-testid={`mnav-child-${(c.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`block py-2 text-sm ${
                  isActive(pathname, c.href) ? "text-mist-gold" : "text-white/80 hover:text-white"
                }`}
              >
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Header() {
  const { nav, settings } = useSite();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const items = (nav && nav.items) || [];
  const logo = (settings && settings.logo_light) || "/logo-white.png";

  // Split link items from CTA items so we can render CTAs as buttons.
  const isCta = (it) => it && it.type === "cta";
  const linkItems = items.filter((it) => !isCta(it));
  const ctaItems = items.filter(isCta);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 transition-all duration-300 bg-mist-navy backdrop-blur-sm border-b border-white/5"
      >
        <div className="container-mist flex items-center justify-between min-h-[72px] xl:min-h-[96px] gap-x-4">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 shrink-0"
            aria-label="MIST Quality Inspection and Logistics — Home"
          >
            <img
              src={logo}
              alt="MIST Quality Inspection and Logistics"
              className="w-[130px] md:w-[150px] lg:w-[165px] xl:w-[180px] h-auto object-contain block max-w-none"
            />
          </Link>

          <nav aria-label="Primary" className="hidden xl:flex items-center gap-x-5 2xl:gap-x-6">
            {linkItems.map((item) =>
              item.children && item.children.length > 0 ? (
                <DesktopDropdown key={item.label} item={item} pathname={pathname} />
              ) : (
                <NavLink
                  key={item.label}
                  to={item.href || "#"}
                  data-testid={item.testid || `nav-${item.label.toLowerCase()}`}
                  className={({ isActive: a }) =>
                    `whitespace-nowrap text-[13px] font-medium tracking-[0.02em] transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mist-gold focus-visible:ring-offset-2 focus-visible:ring-offset-mist-navy rounded-sm ${
                      a ? "text-mist-gold" : "text-white/85 hover:text-white"
                    }`
                  }
                  end={item.href === "/"}
                >
                  {item.label}
                </NavLink>
              )
            )}
            {ctaItems.map((c) => (
              <CtaButton key={c.label} item={c} className="whitespace-nowrap ml-2 h-11 px-6 text-[13px] md:text-sm" />
            ))}
          </nav>

          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className="xl:hidden text-white p-2 -mr-2"
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden fixed inset-x-0 top-[72px] bottom-0 z-40 bg-mist-navy overflow-y-auto"
        >
          <div className="container-mist py-6">
            {linkItems.map((item) => (
              <MobileAccordion
                key={item.label}
                item={item}
                pathname={pathname}
                onNavigate={() => setMobileOpen(false)}
              />
            ))}
            {ctaItems.map((c) => (
              <div key={c.label} className="mt-6 pt-6 border-t border-white/10">
                <CtaButton
                  item={c}
                  onClick={() => setMobileOpen(false)}
                  className="w-full py-3 px-5 text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
